/**
 * Resolves the WebSocket URL for the browser bridge connection.
 *
 * Three cases, in priority order:
 *
 *  - `VITE_BRIDGE_URL` is set: an explicit override, used verbatim. This is
 *    how a controlled staging or testing build points at a non-default
 *    bridge; it applies in both development and production builds.
 *  - Not a production build: the local Node bridge on the page's own
 *    hostname, so it also works from another device on the LAN. When the
 *    dev server was started with a bridge token configured (`npm run
 *    token`), `VITE_ANVIL_TOKEN` carries it through to the browser — the
 *    WebSocket API can't set headers, so it rides along as `?token=`, the
 *    same query param the local bridge itself accepts.
 *  - A production build: fetch a short-lived, single-use viewer ticket from
 *    the same-origin `/api/bridge-ticket` route. The ticket authorizes
 *    exactly one WebSocket upgrade, so nothing about the returned URL is
 *    safe to cache — every reconnect must call the resolver again to get a
 *    fresh one.
 *
 * Failures are classified so the socket layer can retry transients without
 * turning authorization or budget/stop responses into a reload storm.
 * Messages never include the ticket, WebSocket URL, or Access assertion.
 */

const DEV_BRIDGE_PORT = 8787;
export const AUTHORIZATION_ERROR = 'could not authorize the hosted bridge viewer';
export const TICKET_STOP_ERROR = 'hosted bridge viewer recovery is paused';
export const TICKET_FAILURE_TRANSIENT = 'transient';
export const TICKET_FAILURE_AUTHORIZATION = 'authorization';
export const TICKET_FAILURE_STOP = 'stop';

export class BridgeTicketError extends Error {
  /**
   * @param {object} options
   * @param {string} options.message
   * @param {'transient' | 'authorization' | 'stop'} options.kind
   * @param {object | null} [options.storageBudget]
   */
  constructor({ message, kind, storageBudget = null }) {
    super(message);
    this.name = 'BridgeTicketError';
    this.kind = kind;
    this.reconnect = kind === TICKET_FAILURE_TRANSIENT;
    this.storageBudget = storageBudget;
  }
}

export function ticketFailureKind(error) {
  if (error instanceof BridgeTicketError) return error.kind;
  if (error?.name === 'BridgeTicketError' && typeof error.kind === 'string') {
    return error.kind;
  }
  return TICKET_FAILURE_TRANSIENT;
}

export function shouldRetryTicketFailure(error) {
  return ticketFailureKind(error) === TICKET_FAILURE_TRANSIENT;
}

function readStorageBudget(body) {
  const budget = body?.storageBudget;
  if (budget === null || typeof budget !== 'object' || Array.isArray(budget)) {
    return null;
  }
  return budget;
}

function safeTicketErrorMessage(body, fallback) {
  const error = body?.error;
  if (typeof error !== 'string' || error.length === 0 || error.length > 280) {
    return fallback;
  }
  if (/ticket|jwt|assertion|bearer|webSocketUrl/i.test(error)) return fallback;
  return error;
}

function isJsonObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function ticketErrorKindFromResponse(response, body) {
  if (response.status === 401 || response.status === 403) {
    return TICKET_FAILURE_AUTHORIZATION;
  }
  // Only a parsed JSON object with explicit reconnect:false is a stop.
  // Cloudflare/HTML 500/502/503/504 and JSON 503s without that flag stay transient.
  if (isJsonObject(body) && body.reconnect === false) {
    return TICKET_FAILURE_STOP;
  }
  return TICKET_FAILURE_TRANSIENT;
}

function ticketErrorFromResponse(response, body) {
  const kind = ticketErrorKindFromResponse(response, body);
  const storageBudget = isJsonObject(body) ? readStorageBudget(body) : null;
  if (kind === TICKET_FAILURE_AUTHORIZATION) {
    return new BridgeTicketError({
      message: AUTHORIZATION_ERROR,
      kind,
      storageBudget,
    });
  }
  if (kind === TICKET_FAILURE_STOP) {
    const windowResetsAt = storageBudget?.windowResetsAt;
    const reason = safeTicketErrorMessage(body, TICKET_STOP_ERROR);
    const message = typeof windowResetsAt === 'string'
      ? `${reason} (window resets at ${windowResetsAt})`
      : reason;
    return new BridgeTicketError({
      message,
      kind,
      storageBudget,
    });
  }
  return new BridgeTicketError({
    message: AUTHORIZATION_ERROR,
    kind,
    storageBudget,
  });
}

/**
 * @param {object} [options]
 * @param {object} [options.env]              import.meta.env-shaped bag; injectable for tests
 * @param {string} [options.hostname]         page hostname used for the local dev default
 * @param {typeof fetch} [options.fetchImpl]  fetch implementation; injectable for tests
 * @returns {() => Promise<string>} call this once per connection generation
 */
export function createBridgeUrlProvider({
  env = (typeof import.meta !== 'undefined' ? import.meta.env : undefined) ?? {},
  hostname = (typeof location !== 'undefined' && location.hostname) || 'localhost',
  fetchImpl = (typeof fetch !== 'undefined' ? fetch : undefined),
} = {}) {
  const override = env?.VITE_BRIDGE_URL;
  if (override) return async () => override;

  if (!env?.PROD) {
    const token = env?.VITE_ANVIL_TOKEN?.trim();
    const developmentUrl = `ws://${hostname || 'localhost'}:${DEV_BRIDGE_PORT}${
      token ? `?token=${encodeURIComponent(token)}` : ''
    }`;
    return async () => developmentUrl;
  }

  return async function resolveHostedBridgeUrl() {
    let response;
    try {
      response = await fetchImpl('/api/bridge-ticket', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });
    } catch {
      throw new BridgeTicketError({
        message: AUTHORIZATION_ERROR,
        kind: TICKET_FAILURE_TRANSIENT,
      });
    }

    let body;
    try {
      body = await response.json();
    } catch {
      throw ticketErrorFromResponse(response, null);
    }

    if (!response.ok || typeof body?.webSocketUrl !== 'string') {
      throw ticketErrorFromResponse(response, body);
    }

    return body.webSocketUrl;
  };
}
